import { navigate, type Route } from '../boot/router';
import { isAdmin, ROLE_LABEL, type AppUser } from '../boot/session';
import { doLogout } from '../data/supabase';

/**
 * 이 시스템 안의 화면을 오가는 탐색 사이드바.
 *
 * ⚠️ 통합표준 3절 / 7절:
 *    "여러 시스템을 오가는 전역 사이드바"가 아니다. 그건 ONE 쉘이 담당한다.
 *    iframe 안에서는 중복을 피하기 위해 CSS(`html.is-embedded`)로 숨긴다.
 *    직접 접속(개발/단독 사용) 시에는 그대로 보인다.
 */

interface NavDef {
  group: string;
  items: { key: Route['name']; label: string; icon: string; route: Route; adminOnly?: boolean }[];
}

const NAV: NavDef[] = [
  {
    group: '작업',
    items: [
      { key: 'dashboard', label: '대시보드', icon: '▤', route: { name: 'dashboard' } },
      { key: 'projects', label: '프로젝트', icon: '▣', route: { name: 'projects' } },
    ],
  },
  {
    group: '라이브러리',
    items: [{ key: 'blocks', label: '블록 관리', icon: '⬚', route: { name: 'blocks' } }],
  },
  {
    group: '관리',
    items: [
      { key: 'activity', label: '활동 로그', icon: '☰', route: { name: 'activity' } },
      { key: 'admin', label: '설정', icon: '⚙', route: { name: 'admin' }, adminOnly: true },
    ],
  },
];

export function Sidebar({ user, active }: { user: AppUser; active: Route['name'] }) {
  const admin = isAdmin(user);

  return (
    <nav className="app-nav-sidebar" aria-label="StoreCAD 화면 탐색">
      {NAV.map((group) => {
        const items = group.items.filter((i) => !i.adminOnly || admin);
        if (items.length === 0) return null;
        return (
          <div className="nav-group" key={group.group}>
            <div className="nav-group__title">{group.group}</div>
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                className="nav-item"
                aria-current={active === item.key ? 'page' : undefined}
                onClick={() => navigate(item.route)}
              >
                <span className="nav-item__icon" aria-hidden="true">
                  {item.icon}
                </span>
                {item.label}
              </button>
            ))}
          </div>
        );
      })}

      {/*
       * 사용자 정보·로그아웃 — 이 사이드바에만 둔다 (상단 표시줄에는 없음).
       * iframe(ONE 통합) 안에서는 이 사이드바 자체가 숨겨지므로 그때는
       * ONE 쉘 자체의 계정·로그아웃을 쓴다.
       */}
      <div className="sidebar-user">
        <span className="sidebar-user__chip" title={user.email}>
          {user.name || user.email}
        </span>
        <span className="sidebar-user__role">{ROLE_LABEL[user.role]}</span>
        <button
          type="button"
          className="btn btn--sm sidebar-user__logout"
          onClick={() => void doLogout()}
        >
          로그아웃
        </button>
      </div>
    </nav>
  );
}
